import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

type TelemetryProps = Record<string, any>

let posthogReady = false
let sentryReady = false

const SENSITIVE_KEY_RE = /(email|phone|token|password|secret|authorization|cookie)/i

function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item))
  }
  if (value && typeof value === 'object') {
    return sanitizeProps(value as TelemetryProps)
  }
  return value
}

function sanitizeProps(input: TelemetryProps): TelemetryProps {
  const output: TelemetryProps = {}
  for (const [key, value] of Object.entries(input)) {
    output[key] = SENSITIVE_KEY_RE.test(key) ? '[redacted]' : sanitizeValue(value)
  }
  return output
}

function getEnvironment(): string {
  return (import.meta.env.VITE_ENV as string | undefined) || (import.meta.env.DEV ? 'development' : 'production')
}

function getRelease(): string | undefined {
  return (
    (import.meta.env.VITE_RELEASE as string | undefined) ||
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (import.meta.env.VITE_GIT_SHA as string | undefined)
  )
}

export function initLandingTelemetry(): void {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  const posthogHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com'
  const environment = getEnvironment()
  const release = getRelease()

  if (sentryDsn && !sentryReady) {
    Sentry.init({
      dsn: sentryDsn,
      release,
      environment,
      enabled: true,
      tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.15,
      beforeSend(event) {
        if (event.user) {
          event.user.email = undefined
          event.user.ip_address = undefined
          if (event.user.username) event.user.username = undefined
        }
        if (event.request?.headers) {
          event.request.headers = sanitizeProps(event.request.headers as TelemetryProps) as Record<string, string>
        }
        if (event.request?.data && typeof event.request.data === 'object') {
          event.request.data = sanitizeProps(event.request.data as TelemetryProps)
        }
        return event
      },
    })
    Sentry.setTag('surface', 'landing')
    Sentry.setTag('environment', environment)
    if (release) {
      Sentry.setTag('release', release)
    }
    sentryReady = true
  }

  if (posthogKey && !posthogReady) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: '2026-01-30',
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
    })
    posthog.register({
      surface: 'landing',
      environment,
      ...(release ? { release } : {}),
    })
    posthogReady = true
  }
}

export function captureLandingEvent(event: string, properties: TelemetryProps = {}): void {
  if (!posthogReady) return
  posthog.capture(event, sanitizeProps(properties))
}

export function captureLandingError(error: unknown, context: TelemetryProps = {}): void {
  if (sentryReady) {
    Sentry.captureException(error, {
      contexts: {
        landing: sanitizeProps(context),
      },
    })
  }
}
